import { CarInsuranceContextState } from '../../components/CarInsurance/context/index';
import { ApprovalDegree, ApprovalOddsState } from '../../types/approvalOdds';
import { CmsProduct, CmsOffer } from '../../types/cmsProduct';
import type { EligibilityOutcome } from '../../types/eligibility';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import { TravelInsuranceQuotesData } from '../../types/travelInsurance';
import { getChooseOneOffers } from '../helpers/offers';
import { JsonObject } from '../json';
import {
  eligibilityContextPart,
  eligibilityOutcomeFor,
} from './eligibilityTracking';

export enum UserActionEnum {
  CLICK = 'click',
  COLLAPSE = 'collapse',
  EXPAND = 'expand',
  HOVER = 'hover',
  SELECT = 'select',
  // The compare checkbox reports the state it moved to (Airtable #2545).
  CHECK = 'check',
  UNCHECK = 'uncheck',
  // element_impression actions (CMS-1976): 'load' = served (rendered/rotated
  // in, viewport-independent), 'view' = viewed (≥50% on screen for ≥1s).
  LOAD = 'load',
  VIEW = 'view',
}

export enum ComponentTypeEnum {
  PRODUCT_CARD = 'product-card',
  ACCORDION = 'accordion',
  TOOLTIP = 'tooltip',
  TABLE = 'table',
  PRODUCT_PAGE = 'product-page',
  DRAWER = 'drawer',
  CARD = 'card',
  TOP_STICKY = 'top-sticky',
  BOTTOM_STICKY = 'bottom-sticky',
  TAB = 'tab',
  DROPDOWN = 'dropdown',
  IMAGE_CAROUSEL = 'image-carousel',
  BUTTON = 'button',
  // TI provider page M3 apply CTAs (CMS-1795). Values come from the A&A
  // componentType dictionary (Airtable #2389 / #2391).
  MINI_PRODUCT_HIGHLIGHT = 'mini-product-highlight',
  PRODUCT_SPOTLIGHT = 'product-spotlight',
  // TI side-by-side comparison: the card's compare checkbox, the compare table and its
  // "Back to top" link (Airtable #2545, #2551–#2554).
  CHECKBOX = 'checkbox',
  RESULT_PAGE_COMPARISON = 'result-page-comparison',
  TEXT_LINK = 'text-link',
}

/**
 * Rating shown on the component the event fired from, reported inside
 * `product_context_parameter` as `rsr-<source>|rsc-<score>|rct-<count>`.
 */
export type RatingContext = {
  source: string;
  score: number;
  count?: number;
};

/** The `items[]` entry every product event starts from; a surface spreads its own fields after it. */
export const productItem = (
  product: CmsProduct,
  index: string | number,
): JsonObject => ({
  item_id: product.title ?? '',
  item_name: product.product_name,
  item_brand: product.provider?.provider_name ?? '',
  index,
});

export const sendProductAnalytics = (props: {
  event: string;
  action: UserActionEnum;
  componentType: ComponentTypeEnum;
  url?: string;
  itemListName?: string;
  componentTitle: string;
  product: CmsProduct;
  index: string | number;
  pushDataLayer: PushDataLayerFn;
  extraData?: JsonObject;
  itemExtraData?: JsonObject;
  approvalDegree?: ApprovalDegree | null;
  approvalDegreeState?: ApprovalOddsState | null;
  ratingContext?: RatingContext;
  /**
   * Drops `product_context_parameter` instead of falling back to the
   * `aov-unavailable|aos-unavailable` default. For surfaces the approval-odds
   * model never covers (TI, CMS-1795), where the placeholder would report a
   * model state that does not exist; the tracking plan wants the key blank.
   */
  omitProductContext?: boolean;
}) => {
  const {
    action,
    componentTitle,
    componentType,
    event,
    product,
    url,
    itemListName,
    index,
    pushDataLayer,
    extraData,
    itemExtraData,
    approvalDegree,
    approvalDegreeState,
    ratingContext,
    omitProductContext,
  } = props;

  const exclusiveAndBundleOffers = getChooseOneOffers(product);

  const rewardCampaignName = getRewardCampaignName(exclusiveAndBundleOffers);
  const rewardCampaignId = getRewardCampaignId(exclusiveAndBundleOffers);
  const productContextParameter = omitProductContext
    ? undefined
    : getProductContextParameter(
        approvalDegree,
        approvalDegreeState,
        ratingContext,
        eligibilityOutcomeFor(props.product)
      );
  pushDataLayer({
    item_list_name: itemListName ?? url,
    ...(extraData ?? {}),
    event,
    action,
    component_type: componentType,
    component_title: componentTitle,
    // These three are spread rather than assigned: a key present with
    // `undefined` still reaches the dataLayer, and the tracking plan asks for
    // each of them only when it actually has a value.
    ...(rewardCampaignName ? { reward_campaign_name: rewardCampaignName } : {}),
    ...(rewardCampaignId ? { reward_campaign_id: rewardCampaignId } : {}),
    ...(productContextParameter
      ? { product_context_parameter: productContextParameter }
      : {}),
    items: [{ ...productItem(product, index), ...(itemExtraData ?? {}) }],
  });
};

export const configureCiFilterApplied = (
  carInsuranceContext?: CarInsuranceContextState | null
) => {
  if (!carInsuranceContext) {
    return {};
  }

  const {
    filters: {
      sort: { value: sort },
      providers: { value: providers },
    },
  } = carInsuranceContext;

  return {
    sort,
    providers: providers,
  };
};

export const configureCiSearchResultParameters = (
  carInsuranceContext?: CarInsuranceContextState | null
) => {
  if (!carInsuranceContext) {
    return {};
  }

  const {
    requestParams: {
      vehicle: {
        value: { makeName, modelName, trim, manufactureYear },
      },
      driver: {
        value: {
          age,
          gender,
          maritalStatus,
          totalDrivingExperience,
          numberOfClaims,
          occupationType,
        },
      },
      ncd: { value: ncd },
      offPeakVehicle: { value: offPeakVehicle },
      offenceFree: { value: offenceFree },
    },
  } = carInsuranceContext;

  const parts = [
    `brand-${makeName}`,
    `model-${modelName}`,
    trim && `cc-${trim}`,
    `year-${manufactureYear}`,
    `exp-${totalDrivingExperience}`,
    `ncd-${ncd}`,
    `age-${age}`,
    `gender-${gender}`,
    `ms-${maritalStatus}`,
    `noc-${numberOfClaims}`,
    offPeakVehicle && `opc-${offPeakVehicle}`,
    occupationType && `jobt-${occupationType}`,
    offenceFree && `ofd-${offenceFree}`,
  ];

  return parts.filter(Boolean).join('|');
};

export const configureTiSearchResultParameters = (
  travelQuotes?: TravelInsuranceQuotesData
): string => {
  if (!travelQuotes) return '';

  const {
    params: {
      startDate,
      endDate,
      insuranceType,
      noOfAdults,
      noOfChildren,
      region,
      travelAsFamily,
    },
  } = travelQuotes;

  const travelers = `ADULT_${noOfAdults},CHILD_${noOfChildren},FAMILY_${
    travelAsFamily ? 1 : 0
  }`;
  // Positional dimension — the consumer splits on '|', so a missing part stays
  // an empty slot instead of being dropped (dropping it would shift every later
  // field). `region` is undefined on the single-trip journey, where the
  // destination is picked as an ISO list instead; without the fallback it
  // serialised as the literal string "undefined".
  const searchResultParameter = [
    insuranceType,
    region,
    travelers,
    startDate,
    endDate,
  ]
    .map((part) => part ?? '')
    .join('|');

  return searchResultParameter;
};

const mapCampaignRewards = (
  exclusiveOffers: CmsOffer[] | undefined,
  mapFunc: (offer: CmsOffer) => string | undefined
): string | undefined => {
  if (!exclusiveOffers?.length) {
    return undefined;
  }

  return exclusiveOffers.map(mapFunc).filter(Boolean).join(', ');
};

export const getRewardCampaignId = (
  exclusiveOffers?: CmsOffer[]
): string | undefined =>
  mapCampaignRewards(exclusiveOffers, (offer) => offer.title);

export const getRewardCampaignName = (
  exclusiveOffers?: CmsOffer[]
): string | undefined =>
  mapCampaignRewards(exclusiveOffers, (offer) => offer.offer_title);

export const getProductContextParameter = (
  approvalDegree?: ApprovalDegree | null,
  approvalDegreeState?: ApprovalOddsState | null,
  ratingContext?: RatingContext,
  /** Only on a page offering the eligibility check — see `eligibilityTracking`. */
  eligibilityOutcome?: EligibilityOutcome
): string => {
  const aovMap: Record<string, string> = {
    HIGH: 'high',
    MEDIUM: 'medium',
    LOW: 'low',
    UNKNOWN: 'unavailable',
  };

  const aosMap: Record<string, string> = {
    fresh: 'live',
    expired: 'expired',
    guest: 'locked',
    tooStale: 'locked',
    noConsent: 'locked',
  };

  const aov = aovMap[approvalDegree ?? 'UNKNOWN'] ?? 'unavailable';
  const aos = approvalDegreeState
    ? aosMap[approvalDegreeState] ?? 'unavailable'
    : 'unavailable';

  // Approval odds is the default segment, so a caller passing no context at all still
  // reports `aov-unavailable|aos-unavailable` (CMS-1758).
  const hasApprovalOdds = !!approvalDegree || !!approvalDegreeState;
  const parts = [
    ...(hasApprovalOdds || !ratingContext ? [`aov-${aov}`, `aos-${aos}`] : []),
    ...(ratingContext
      ? [
          `rsr-${ratingContext.source}`,
          `rsc-${ratingContext.score.toFixed(1)}`,
          ...(ratingContext.count != null
            ? [`rct-${ratingContext.count}`]
            : []),
        ]
      : []),
    ...(eligibilityOutcome ? [eligibilityContextPart(eligibilityOutcome)] : []),
  ];

  return parts.join('|');
};
